import { Col, Row } from 'antd';
import { groupBy } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { IntegrationField, SalesforceIntegrationId } from 'api/integrations';
import { Button, ButtonGroup } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Loading } from 'components/atoms/loading';
import { Control, Option, Select } from 'components/atoms/select';
import { Menu } from 'components/atoms/select/popover/trigger-components';
import {
  Drawer,
  DrawerContent,
} from 'components/organisms/timesheets/common/drawer';
import { useTranslation } from 'hooks/use-translation';
import {
  SALESFORCE_AUTOMATION_TEMPLATE_PLACEHOLDERS_FIELDS_QUERY_KEY,
  useTemplatePlaceholders,
} from 'queries/salesforce-integration';
import { RLProjectFieldMapping } from '../../../types';
import { VirtualizedMenuList } from '../../../virtualised-list';
import { StatusTypes } from '../use-automation-create';
import { DisplayDynamicValue } from './common';

type ActionDrawerProps = {
  fields: IntegrationField[];
  fieldsStatus: StatusTypes;
  modalVisibility: boolean;
  value?: RLProjectFieldMapping;
  handleModalVisibility: (visibility: boolean) => void;
  handleSelection: (actions: RLProjectFieldMapping) => void;
  salesforceObj: Option;
};

const IntegrationFieldId = {
  StartDate: 'startDate',
  Template: 'templateId',
};

const StyledMenu = styled(Menu)`
  box-shadow: var(--shadow-08);
`;

const StyledLoading = styled(Loading)`
  margin-left: 45%;
`;

const StyledDrawer = styled(Drawer)`
  & .ant-drawer-body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    -webkit-box-align: stretch;
    align-items: stretch;
    padding: 0;
  }
  ${DrawerContent.Title} {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: var(--coolest-gray-10);
    padding: var(--spacing-05);
    padding-bottom: var(--spacing-07);
  }
  ${DrawerContent.Footer} {
    position: sticky;
    border-top: 1px solid var(--border-color);
    bottom: 0;
    padding: var(--spacing-05);
    z-index: 1;
    background-color: var(--white-100);
  }
`;

const Wrapper = styled.div`
  ${DrawerContent.Content} {
    padding: var(--spacing-05);
    padding-top: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }
`;

export const ActionDrawer: FC<ActionDrawerProps> = ({
  fields,
  fieldsStatus,
  modalVisibility,
  value,
  handleModalVisibility,
  handleSelection,
  salesforceObj,
}) => {
  const [localPlaceholder, setLocalPlaceholders] =
    useState<IntegrationField[]>();
  const [currentTemplateId, setCurrentTemplateId] = useState(value?.templateId);
  const [searchedText, setSearchedText] = useState<string>('');
  const { integrationId } = useParams<SalesforceIntegrationId>();
  const { data, status } = useTemplatePlaceholders(
    Number(integrationId),
    salesforceObj?.value.toString(),
    currentTemplateId?.valueStr,
    {
      enabled: !!currentTemplateId,
    },
  );
  const queryClient = useQueryClient();
  const [rlFieldMappingForm] = Form.useForm<RLProjectFieldMapping>();
  const handleSave = () => {
    rlFieldMappingForm.validateFields().then((resp) => {
      handleSelection(resp);
      // To update query
      queryClient.setQueriesData(
        SALESFORCE_AUTOMATION_TEMPLATE_PLACEHOLDERS_FIELDS_QUERY_KEY,
        localPlaceholder,
      );
    });
  };

  useEffect(() => {
    if (status === 'success' && data) {
      setLocalPlaceholders(data);
    }
  }, [status, data]);

  const handleSelectTemplate = ({ templateId }) => {
    if (templateId) {
      setCurrentTemplateId(templateId);
    }
    if (templateId === null) {
      setLocalPlaceholders([]);
      setCurrentTemplateId(null);
    }
  };
  const requiredText = useTranslation('input.required');
  const excludeSquareBrackFields = ['templateId'];
  const rocketlaneText = useTranslation('rocketlane');
  const getOptionsInFormat = (field) => {
    const tempObj = groupBy(
      field.metaData?.options,
      'valueMeta.parentFieldName',
    );

    let groupedOptions: any[] = Object.keys(tempObj).map((key) => {
      return {
        label:
          field.fieldName === 'templateId'
            ? rocketlaneText
            : key !== 'undefined'
            ? key
            : salesforceObj.label,
        options: [
          ...tempObj[key].map((option) => {
            return {
              ...option,
              label: excludeSquareBrackFields.includes(field.fieldName)
                ? option.label
                : option.label.indexOf(']') > -1
                ? option.label.split(']')[1].trim()
                : option.label,
              value: option.valueStr,
            };
          }),
        ],
      };
    });

    const nativeFields: any = groupedOptions.find(
      (option) => option.label === salesforceObj.label,
    );

    const rocketLaneFields: any = groupedOptions.find(
      (option) => option.label.toLowerCase() === rocketlaneText.toLowerCase(),
    );

    if (rocketLaneFields !== undefined && nativeFields !== undefined) {
      groupedOptions = [
        rocketLaneFields,
        nativeFields,
        ...groupedOptions.filter(
          (option) =>
            option.label !== salesforceObj.label &&
            option.label.toLowerCase() !== rocketlaneText.toLowerCase(),
        ),
      ];
    } else if (nativeFields !== undefined) {
      groupedOptions = [
        nativeFields,
        ...groupedOptions.filter(
          (option) => option.label !== salesforceObj.label,
        ),
      ];
    }

    return groupedOptions;
  };

  return (
    <StyledDrawer
      visible={modalVisibility}
      onClose={() => handleModalVisibility(false)}
      destroyOnClose
    >
      <Wrapper>
        <DrawerContent.Title>
          <FormattedMessage id="settings.integrations.sf.automation.create.withRl.modal.title" />
        </DrawerContent.Title>
        <DrawerContent.Content>
          <>
            {fieldsStatus === 'loading' ? (
              <Loading />
            ) : (
              <Row justify="space-around" align="middle">
                <Col span={24}>
                  <Form
                    onValuesChange={(e) => handleSelectTemplate(e)}
                    layout="vertical"
                    form={rlFieldMappingForm}
                    autoComplete="off"
                    style={{ marginTop: 'var(--spacing-05)' }}
                    initialValues={value}
                  >
                    {fields.map((field) => {
                      return (
                        <Form.Item
                          name={field.integrationFieldId}
                          key={field.integrationFieldId}
                          label={field.fieldLabel}
                          rules={[
                            {
                              required: field?.required,
                              message: requiredText,
                            },
                            ({ getFieldValue }) => ({
                              validator(_, fieldValue) {
                                // if template is selected, start date is requried
                                if (
                                  field.integrationFieldId ===
                                    IntegrationFieldId.StartDate &&
                                  !fieldValue &&
                                  getFieldValue(IntegrationFieldId.Template)
                                ) {
                                  return Promise.reject(requiredText);
                                }

                                return Promise.resolve();
                              },
                            }),
                          ]}
                          dependencies={
                            field.integrationFieldId ===
                            IntegrationFieldId.StartDate
                              ? [IntegrationFieldId.Template]
                              : undefined
                          }
                        >
                          <Select
                            isMulti={field.fieldName === 'teamMembers'}
                            components={{
                              IndicatorSeparator: null,
                              Control,
                              Menu: StyledMenu,
                              MenuList: VirtualizedMenuList,
                            }}
                            menuPlacement="auto"
                            formatOptionLabel={DisplayDynamicValue}
                            isClearable
                            tabSelectsValue={false}
                            options={getOptionsInFormat(field)}
                            grouped
                            searchedText={searchedText}
                            onInputChange={(input) => setSearchedText(input)}
                          />
                        </Form.Item>
                      );
                    })}
                    {status === 'loading' ? (
                      <>{currentTemplateId && <StyledLoading />}</>
                    ) : (
                      localPlaceholder?.map((field) => (
                        <Form.Item
                          name={field.integrationFieldId}
                          key={field.integrationFieldId}
                          label={field.fieldLabel}
                          rules={[
                            {
                              required: field?.required,
                              message: requiredText,
                            },
                            ({ getFieldValue }) => ({
                              validator(_, fieldValue) {
                                // if template is selected, start date is requried
                                if (
                                  field.integrationFieldId ===
                                    IntegrationFieldId.StartDate &&
                                  !fieldValue &&
                                  getFieldValue(IntegrationFieldId.Template)
                                ) {
                                  return Promise.reject(requiredText);
                                }

                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Select
                            components={{
                              IndicatorSeparator: null,
                              Control,
                              Menu: StyledMenu,
                              MenuList: VirtualizedMenuList,
                            }}
                            menuPlacement="auto"
                            formatOptionLabel={DisplayDynamicValue}
                            isClearable
                            tabSelectsValue={false}
                            options={getOptionsInFormat(field)}
                            grouped
                            searchedText={searchedText}
                            onInputChange={(input) => setSearchedText(input)}
                          />
                        </Form.Item>
                      ))
                    )}
                  </Form>
                </Col>
              </Row>
            )}
          </>
        </DrawerContent.Content>
      </Wrapper>
      <DrawerContent.Footer>
        <ButtonGroup $reversed>
          <Button type="primary" onClick={handleSave}>
            <FormattedMessage id="save" />
          </Button>
          <Button onClick={() => handleModalVisibility(false)}>
            <FormattedMessage id="cancel" />
          </Button>
        </ButtonGroup>
      </DrawerContent.Footer>
    </StyledDrawer>
  );
};
